<template>
  <v-menu
    max-width="500px"
    offset-y
    top
    internal-activator
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ on }">
      <v-row
        class="timeline-clock"
        :class="{ 'striped': striped, 'opacity--03': !clock.active || clock.parent_disabled }"
        no-gutters
        v-on="on"
      >
        <!--<editor-fold desc="Title part">-->
        <v-col
          cols="3"
          class="py-2 px-4"
          style="border-left: 4px solid"
          :style="{ borderColor: color }"
        >
          <v-row
            v-if="clock.content_type === CONTENT_TYPE_MUSIC"
            justify="start"
            align="center"
            no-gutters
            style="min-width: 0"
          >
            <v-col class="caption text-truncate">
              {{ clock.playlists[0].title }}
            </v-col>
            <v-col
              v-if="clock.playlists.length > 1"
              cols="auto"
              class="caption font-weight-medium pl-1"
            >
              + {{ clock.playlists.length - 1 }} playlists
            </v-col>
            <v-col
              v-if="clock.parent_clock"
              cols="auto"
              class="pl-1"
            >
              <v-chip
                color="grey lighten-2"
                x-small
                class="ma-0"
              >
                Parent
              </v-chip>
            </v-col>
          </v-row>
          <v-row
            v-else-if="clock.content_type === CONTENT_TYPE_MARKETING"
            no-gutters
            class="caption"
            style="min-width: 0"
          >
            <v-col
              v-if="clock.individual_files.length > 0"
              class="text-truncate"
            >
              {{ clock.individual_files[0].description }} ({{ clock.individual_files[0].original_file }})
            </v-col>
            <v-col
              v-else-if="clock.playlists.length > 0"
              class="text-truncate"
            >
              {{ clock.playlists[0].title }}
            </v-col>
            <v-col
              cols="auto"
              class="caption font-weight-medium pl-2"
            >
              <v-chip
                v-if="clock.parent_disabled"
                x-small
                color="grey darken-2"
                text-color="white"
              >
                Disabled
              </v-chip>
              <v-chip
                v-else-if="clock.active"
                x-small
                color="secondary"
              >
                Active
              </v-chip>
              <v-chip
                v-else
                x-small
                color="grey darken-2"
                text-color="white"
              >
                Inactive
              </v-chip>
              <v-chip
                v-if="clock.parent_clock"
                x-small
                color="grey lighten-2"
                class="ma-0"
              >
                Parent
              </v-chip>
            </v-col>
          </v-row>
          <v-row
            v-else-if="clock.content_type === CONTENT_TYPE_PA"
            no-gutters
            class="caption"
            style="min-width: 0"
          >
            <div
              v-if="clock.individual_files.length > 0"
            >
              PA - {{ clock.individual_files[0].title }}
            </div>
          </v-row>
        </v-col>
        <!--</editor-fold>-->
        <!--<editor-fold desc="Timeline part">-->
        <v-col
          ref="timelineContainer"
          cols="9"
          class="pr-3"
          style="position: relative"
        >
          <!--<editor-fold desc="Timeline grid">-->
          <v-row
            no-gutters
            class="fill-height"
          >
            <v-col
              v-for="hour in hours"
              :key="hour"
              class="caption text--secondary fill-height"
              style="position: relative"
            >
              <div
                style="border-left: 1px solid rgba(0, 0, 0, 0.1);"
                class="fill-height"
              />
              <div
                v-if="hour === 23"
                style="position: absolute; width: 100%; top: 0; border-right: 1px solid rgba(0, 0, 0, 0.1);"
                class="fill-height"
              />
            </v-col>
          </v-row>
          <!--</editor-fold>-->
          <!--<editor-fold desc="Event">-->
          <div
            class="timeline-clock__line rounded-lg py-1 px-2 caption white--text"
            :style="{ left: `${leftPosition}px`, width: `${width}px`, backgroundColor: color }"
          >
            <v-row no-gutters>
              <v-col
                v-if="width > 200 && clock.playback_type !== PLAYBACK_TYPE_TCP_MESSAGE"
                cols="auto"
              >
                <span
                  v-for="day in days"
                  :key="'small_day_' + day.day"
                  class="mr-1 font-weight-medium"
                  :class="{ 'opacity--03': !clock.days.includes(day.day) && clock.playback_type !== PLAYBACK_TYPE_TCP_MESSAGE }"
                >
                  {{ day.short }}
                </span>
              </v-col>
              <v-col
                v-if="width > 360 && clock.playback_type === PLAYBACK_TYPE_TCP_MESSAGE"
                cols="auto"
                class="opacity--07"
              >
                play on <strong>{{ clock.playback_tcp_message }}</strong> message
              </v-col>
              <v-col
                v-if="width > 360 && clock.playback_type === PLAYBACK_TYPE_AFTER_SONGS"
                cols="auto"
                class="opacity--07"
              >
                &middot; play <strong>{{ clock.playback_content_count }}</strong> every <strong>{{ clock.playback_every_songs }}</strong> songs
                (<strong>{{ clock.playback_content_count / (clock.playback_content_count + clock.playback_every_songs) | percent }}</strong>)
              </v-col>
              <v-col
                v-if="width > 360 && clock.playback_type === PLAYBACK_TYPE_AFTER_MINUTES"
                cols="auto"
                class="opacity--07"
              >
                &middot; play <strong>{{ clock.playback_content_count }}</strong> every <strong>{{ clock.playback_every_minutes }}</strong> mins
              </v-col>
              <v-spacer />
              <v-col
                v-if="width > 75 && clock.playback_type !== PLAYBACK_TYPE_TCP_MESSAGE"
                cols="auto"
              >
                {{ start.format('HH:mm') }} - {{ end.format('HH:mm') }}
              </v-col>
            </v-row>
          </div>
          <!--</editor-fold>-->
        </v-col>
        <!--</editor-fold>-->
      </v-row>
    </template>
    <!--<editor-fold desc="Menu">-->
    <v-card
      elevation="20"
      tile
      style="border: 2px solid"
      :style="{ borderColor: color }"
    >
      <v-card-text class="pa-3">
        <v-row no-gutters>
          <v-col cols="auto">
            <div
              :style="{ color: color }"
              class="body-2"
            >
              <div v-if="clock.content_type === CONTENT_TYPE_MUSIC && clock.playback_type === PLAYBACK_TYPE_AFTER_SONGS">
                Music Powerplay
              </div>
              <div v-else-if="clock.content_type === CONTENT_TYPE_MUSIC">
                Music
              </div>
              <div v-else-if="clock.content_type === CONTENT_TYPE_MARKETING">
                Marketing
              </div>
              <div v-else-if="clock.content_type === CONTENT_TYPE_PA">
                Public Announcement
              </div>
            </div>
          </v-col>
          <v-spacer />
          <v-col
            cols="auto"
            class="text-no-wrap"
          >
            <span
              v-for="day in days"
              :key="'menu_small_day_' + day.day"
              class="body-2"
              :style="{ color: clock.days.includes(day.day) || clock.playback_type === PLAYBACK_TYPE_TCP_MESSAGE ? color : undefined }"
              :class="{ 'opacity--03': !clock.days.includes(day.day) && clock.playback_type !== PLAYBACK_TYPE_TCP_MESSAGE, 'font-weight-medium': clock.days.includes(day.day) || clock.playback_type === PLAYBACK_TYPE_TCP_MESSAGE }"
            >
              {{ day.short }}
            </span>
            &middot;
            <span
              v-if="clock.end_time_at"
              class="body-2"
              :style="{ color: color }"
            >{{ start.format('HH:mm') }} - {{ end.format('HH:mm') }}</span>
            <span
              v-else
              class="body-2"
              :style="{ color: color }"
            >
              {{ start.format('HH:mm') }}
            </span>
          </v-col>
          <v-col
            v-if="!clock.active"
            cols="auto"
            class="pl-2"
          >
            <v-chip
              small
              class="ma-0"
            >
              Inactive
            </v-chip>
          </v-col>
        </v-row>
        <v-divider class="my-2" />
        <div v-if="clock.content_type === CONTENT_TYPE_MUSIC">
          <div
            v-for="playlist in clock.playlists"
            :key="playlist.id"
            class="caption"
          >
            {{ playlist.title }}
          </div>
        </div>
        <div v-else-if="clock.content_type === CONTENT_TYPE_MARKETING">
          <v-row
            v-for="playlist in clock.playlists"
            :key="playlist.id"
            class="caption"
            no-gutters
          >
            <v-col>
              Marketing Campaign
            </v-col>
            <v-col cols="auto">
              {{ playlist.title }}
            </v-col>
          </v-row>
          <div
            v-for="file in clock.individual_files"
            :key="file.id"
            class="caption"
          >
            {{ file.description }} ({{ file.original_file }})
          </div>
        </div>
        <div v-else-if="clock.content_type === CONTENT_TYPE_PA">
          <div
            v-for="file in clock.individual_files"
            :key="file.id"
            class="caption"
          >
            <div class="font-weight-bold">
              {{ file.original_file }}
            </div>
            <div>
              {{ file.description }}
            </div>
          </div>
        </div>
        <div v-if="clock.content_type === CONTENT_TYPE_PA && clock.playback_type === PLAYBACK_TYPE_TCP_MESSAGE">
          <v-row
            class="caption"
            no-gutters
          >
            <v-col>
              TCP message
            </v-col>
            <v-col
              cols="auto"
              class="font-weight-bold"
            >
              {{ clock.playback_tcp_message }}
            </v-col>
          </v-row>
        </div>
        <div v-if="clock.content_type === CONTENT_TYPE_MARKETING">
          <v-divider class="my-2" />
          <v-row
            no-gutters
            class="caption"
          >
            <v-col>
              Dates active
            </v-col>
            <v-col
              cols="auto"
              class="font-weight-bold"
            >
              <date-range-label
                :from="clock.start_date_at"
                :until="clock.end_date_at"
              />
            </v-col>
          </v-row>
        </div>
        <v-divider class="my-2" />
        <v-row no-gutters>
          <v-spacer />
          <v-col cols="auto">
            <v-btn
              v-if="!clock.parent_clock"
              color="primary"
              class="ma-0"
              small
              @click="$emit('edit', clock)"
            >
              <v-icon
                left
                x-small
              >
                fa fa-pen
              </v-icon>
              Edit
            </v-btn>
            <v-switch
              v-else
              label="Disable in child"
              class="body-2 pa-0 ma-0"
              color="secondary"
              hide-details
              :value="clock.parent_disabled"
              :input-value="clock.parent_disabled"
              @click="toggleDisabled()"
            >
              <template v-slot:label>
                <span class="grey--text text--darken-2 body-2">
                  Disable in child
                </span>
              </template>
            </v-switch>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <!--</editor-fold>-->
  </v-menu>
</template>

<script>
import Clock from '@/models/ClockModel'
import DateRangeLabel from '@/components/helpers/DateRangeLabel'
import Radios from '@/services/Radios'

export default {
  name: 'TimelineClock',
  components: { DateRangeLabel },
  props: {
    clock: {
      type: Object,
      required: true
    },
    striped: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      hourWidth: 0,
      start: undefined,
      end: undefined
    }
  },
  computed: {
    durationInHours() {
      return this.end.diff(this.start, 'hour', true)
    },
    startHour() {
      return this.start.diff(this.$dayjs('00:00:00', 'HH:mm:ss'), 'hour', true)
    },
    width() {
      if (!this.hourWidth) return 0

      return this.durationInHours * this.hourWidth
    },
    leftPosition() {
      if (!this.hourWidth) return 0

      return this.startHour * this.hourWidth
    },
    color() {
      return Clock.colors[this.clock.content_type][this.clock.playback_type]
    }
  },
  watch: {
    clock(newClock) {
      if (!newClock) return

      this.setStartEnd()
    }
  },
  created() {
    this.hours = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23]
    this.days = Clock.days
    this.setStartEnd()

    this.PLAYBACK_TYPE_DEFAULT = Clock.PLAYBACK_TYPE_DEFAULT
    this.PLAYBACK_TYPE_AFTER_SONGS = Clock.PLAYBACK_TYPE_AFTER_SONGS
    this.PLAYBACK_TYPE_AFTER_MINUTES = Clock.PLAYBACK_TYPE_AFTER_MINUTES
    this.PLAYBACK_TYPE_TCP_MESSAGE = Clock.PLAYBACK_TYPE_TCP_MESSAGE

    this.CONTENT_TYPE_MUSIC = Clock.CONTENT_TYPE_MUSIC
    this.CONTENT_TYPE_MARKETING = Clock.CONTENT_TYPE_MARKETING
    this.CONTENT_TYPE_PA = Clock.CONTENT_TYPE_PA
  },
  mounted() {
    setTimeout(() => {
      this.hourWidth = (this.$refs.timelineContainer.clientWidth - 12) / 24
    }, 0)
  },
  methods: {
    setStartEnd() {
      if (this.clock.playback_type === Clock.PLAYBACK_TYPE_TCP_MESSAGE) {
        this.start = this.$dayjs('00:00:00', 'HH:mm:ss')
        this.end = this.$dayjs('23:59:00', 'HH:mm:ss')
      } else {
        this.start = this.$dayjs(this.clock.start_time_at, 'HH:mm:ss')
        this.end = this.$dayjs(this.clock.end_time_at, 'HH:mm:ss')
      }
    },
    async toggleDisabled() {
      if (!this.clock.parent_clock) return

      try {
        await Radios.toggleDisabled(this.clock.radio_id, this.clock.id)

        this.$emit('clock:updated')

        this.$store.dispatch('alert', {
          text: this.clock.parent_disabled ? 'Clock successfully re-enabled' : 'Parent clock disabled',
          color: 'success'
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Error toggling disabled state',
          color: 'error'
        })

        throw e
      }
    }
  }
}
</script>

<style scoped>

</style>
